/**
 * Nango connected-app HITL cards: which tools get a custom review UI, and how
 * the clinician's in-card edits become a full ``modified_input`` replacement.
 *
 * Mail / calendar / files are bound directly. Docs, sheets, OneNote and Teams
 * writes usually arrive through ``integrations.apps.write`` with the leaf tool
 * in ``tool`` / ``arguments``.
 */

import type { Artifact } from '../../data/projects/artifacts/state';
import {
  blocksOfKind,
  readPresentation,
  type EmailAttachment,
  type EmailBlock,
  type EmailSender,
} from './tool-call-presentation';

export const NANGO_MAIL_APPROVAL_TOOLS = [
  'integrations.mail.send',
  'integrations.mail.create_draft',
  'integrations.mail.reply',
] as const;

export const NANGO_CALENDAR_APPROVAL_TOOLS = [
  'integrations.calendar.create',
  'integrations.calendar.update',
  'integrations.calendar.delete',
] as const;

export const NANGO_FILE_APPROVAL_TOOLS = ['integrations.files.save'] as const;

export const NANGO_WORKSPACE_WRITE_APPROVAL_TOOLS = [
  'integrations.apps.write',
  'integrations.docs.create',
  'integrations.docs.append_text',
  'integrations.sheets.append_row',
  'integrations.onenote.create_page',
  'integrations.teams.post_message',
] as const;

export type NangoMailApprovalTool = (typeof NANGO_MAIL_APPROVAL_TOOLS)[number];
export type NangoCalendarApprovalTool = (typeof NANGO_CALENDAR_APPROVAL_TOOLS)[number];
export type NangoFileApprovalTool = (typeof NANGO_FILE_APPROVAL_TOOLS)[number];
export type NangoWorkspaceWriteApprovalTool =
  (typeof NANGO_WORKSPACE_WRITE_APPROVAL_TOOLS)[number];

export type NangoConnectorApprovalKind = 'mail' | 'calendar' | 'file' | 'workspace';

export type NangoWorkspaceWriteAction = 'create' | 'edit' | 'delete' | 'send';

const MAIL_SET: ReadonlySet<string> = new Set(NANGO_MAIL_APPROVAL_TOOLS);
const CALENDAR_SET: ReadonlySet<string> = new Set(NANGO_CALENDAR_APPROVAL_TOOLS);
const FILE_SET: ReadonlySet<string> = new Set(NANGO_FILE_APPROVAL_TOOLS);
const WORKSPACE_SET: ReadonlySet<string> = new Set(NANGO_WORKSPACE_WRITE_APPROVAL_TOOLS);

export const isNangoConnectorApprovalTool = (name: string): boolean =>
  MAIL_SET.has(name) ||
  CALENDAR_SET.has(name) ||
  FILE_SET.has(name) ||
  WORKSPACE_SET.has(name);

export const resolveNangoApprovalLeafTool = (
  toolName: string,
  toolInput: Record<string, unknown>
): string => {
  if (toolName === 'integrations.apps.write' && typeof toolInput.tool === 'string') {
    return toolInput.tool;
  }
  return toolName;
};

export const nangoConnectorApprovalKind = (
  toolName: string,
  toolInput: Record<string, unknown> = {}
): NangoConnectorApprovalKind | null => {
  const leaf = resolveNangoApprovalLeafTool(toolName, toolInput);
  if (MAIL_SET.has(leaf)) return 'mail';
  if (CALENDAR_SET.has(leaf)) return 'calendar';
  if (FILE_SET.has(leaf)) return 'file';
  if (leaf !== 'integrations.apps.write' && WORKSPACE_SET.has(leaf)) {
    return 'workspace';
  }
  // Directory writes whose leaf is not a known mail/calendar/file/workspace
  // tool still get the workspace card — except Availity coverage, which must
  // keep the generic payload review.
  if (
    toolName === 'integrations.apps.write' &&
    !leaf.startsWith('integrations.coverage.')
  ) {
    return 'workspace';
  }
  return null;
};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

export const parseAddressList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter(
      (entry): entry is string => typeof entry === 'string' && Boolean(entry.trim())
    );
  }
  if (typeof value === 'string') {
    return value
      .split(/[,;]/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
};

export const formatAddressList = (value: unknown): string =>
  parseAddressList(value).join(', ');

export const parseAddressListInput = (value: string): string[] => parseAddressList(value);

/** ``datetime-local`` wall clock in the clinician's timezone. */
export const toDatetimeLocalValue = (iso: unknown): string => {
  if (typeof iso !== 'string' || !iso.trim()) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(iso)) return iso;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    const naive = iso.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
    return naive?.[1] ?? '';
  }
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

export const fromDatetimeLocalValue = (local: string): string => {
  if (!local.trim()) return '';
  const parsed = new Date(local);
  return Number.isNaN(parsed.getTime()) ? local : parsed.toISOString();
};

/**
 * Keep the gated ISO (or naive wall-clock) when the clinician has not edited
 * the datetime-local field. Rebuilding through Date always would shift naive
 * values into UTC and drop seconds.
 */
export const resolveDatetimeForApprove = (
  originalIso: string,
  localValue: string
): string => {
  if (!localValue.trim()) return originalIso;
  if (localValue === toDatetimeLocalValue(originalIso)) return originalIso;
  return fromDatetimeLocalValue(localValue);
};

export interface MailComposeApprovalFields {
  kind: 'compose';
  toolName: 'integrations.mail.send' | 'integrations.mail.create_draft';
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
}

export interface MailReplyApprovalFields {
  kind: 'reply';
  toolName: 'integrations.mail.reply';
  messageId: string;
  body: string;
}

export type MailApprovalFields = MailComposeApprovalFields | MailReplyApprovalFields;

/**
 * The presentation's `email` block for a reply: who it goes to (the original
 * message's Reply-To, else its sender) and the thread subject.
 */
export interface MailReplyApprovalPreview {
  to: string[];
  subject: string | null;
}

/** One file the server sealed into the approval's run before the card opened. */
export interface MailAttachmentPreview {
  filename: string;
  mediaType: string;
  sizeBytes: number;
  attachmentId: string | null;
}

export const countRequestedMailAttachments = (
  toolName: string,
  toolInput: Record<string, unknown>
): number => {
  const source =
    toolName === 'integrations.apps.write' && isRecord(toolInput.arguments)
      ? toolInput.arguments
      : toolInput;
  return Array.isArray(source.attachments) ? source.attachments.length : 0;
};

const readMailEmailBlock = (
  context: Record<string, unknown> | undefined
): EmailBlock | undefined => blocksOfKind(readPresentation(context).blocks, 'email')[0];

const toMailAttachmentPreview = (file: EmailAttachment): MailAttachmentPreview => ({
  filename: file.filename,
  mediaType: file.media_type,
  sizeBytes: file.size_bytes,
  attachmentId: file.attachment_id ?? null,
});

/**
 * Files the email block lists, or `null` while the context has no email block
 * yet. The block drops invalid rows, so a short list fails
 * `mailAttachmentsVerified` and the card cannot send.
 */
export const readMailAttachmentPreview = (
  context: Record<string, unknown> | undefined
): MailAttachmentPreview[] | null =>
  readMailEmailBlock(context)?.attachments.map(toMailAttachmentPreview) ?? null;

/** A card with requested files only approves when every one has a preview. */
export const mailAttachmentsVerified = (
  requestedCount: number,
  preview: readonly MailAttachmentPreview[]
): boolean => requestedCount === 0 || preview.length === requestedCount;

export type MailAttachmentVerificationStatus = 'idle' | 'pending' | 'verified' | 'failed';

/**
 * Pending means attachments were requested and neither the snapshot nor the
 * live event has an email block yet. A present block whose files do not match
 * the request has failed. A source that matches wins over a source that does not.
 */
export const resolveMailAttachmentVerification = (
  requestedCount: number,
  sources: readonly (Record<string, unknown> | undefined)[]
): { status: MailAttachmentVerificationStatus; preview: MailAttachmentPreview[] } => {
  if (requestedCount === 0) {
    return { status: 'idle', preview: [] };
  }
  let sawPreview = false;
  for (const source of sources) {
    const preview = readMailAttachmentPreview(source);
    if (preview === null) continue;
    sawPreview = true;
    if (mailAttachmentsVerified(requestedCount, preview)) {
      return { status: 'verified', preview };
    }
  }
  return sawPreview
    ? { status: 'failed', preview: [] }
    : { status: 'pending', preview: [] };
};

/** The approved file in the run-attachment viewer, or `null` when the row or the run has no id. */
export const mailAttachmentArtifact = (
  agentRunId: string | undefined,
  file: MailAttachmentPreview
): Artifact | null =>
  agentRunId && file.attachmentId
    ? {
        type: 'run-attachment',
        agentRunId,
        attachmentId: file.attachmentId,
        filename: file.filename,
      }
    : null;

/**
 * The mailbox the send, draft, or reply will use, from the email block. The
 * first context with one wins, so a live event can fill in a snapshot that has
 * not caught up. The server binds that mailbox on approve.
 */
export const readMailSender = (
  ...contexts: (Record<string, unknown> | undefined)[]
): EmailSender | null => {
  for (const context of contexts) {
    const sender = readMailEmailBlock(context)?.sender;
    if (sender) return sender;
  }
  return null;
};

export const readMailReplyPreview = (
  context: Record<string, unknown> | undefined
): MailReplyApprovalPreview | null => {
  const email = readMailEmailBlock(context);
  const to = (email?.to ?? []).map((address) => address.trim()).filter(Boolean);
  if (!email || to.length === 0) return null;
  return { to, subject: email.subject.trim() || null };
};

export const readMailApprovalFields = (
  toolName: string,
  toolInput: Record<string, unknown>
): MailApprovalFields | null => {
  const leaf = resolveNangoApprovalLeafTool(toolName, toolInput);
  const source =
    toolName === 'integrations.apps.write' && isRecord(toolInput.arguments)
      ? toolInput.arguments
      : toolInput;
  if (leaf === 'integrations.mail.reply') {
    return {
      kind: 'reply',
      toolName: leaf,
      messageId: asString(source.message_id),
      body: asString(source.body),
    };
  }
  if (leaf === 'integrations.mail.send' || leaf === 'integrations.mail.create_draft') {
    return {
      kind: 'compose',
      toolName: leaf,
      to: parseAddressList(source.to),
      cc: parseAddressList(source.cc),
      bcc: parseAddressList(source.bcc),
      subject: asString(source.subject),
      body: asString(source.body),
    };
  }
  return null;
};

export const buildMailModifiedInput = (
  original: Record<string, unknown>,
  fields: MailApprovalFields
): Record<string, unknown> => {
  if (fields.kind === 'reply') {
    return applyFieldPatch(original, { body: fields.body });
  }
  return applyFieldPatch(original, {
    to: fields.to,
    cc: fields.cc,
    bcc: fields.bcc,
    subject: fields.subject,
    body: fields.body,
  });
};

export interface CalendarApprovalFields {
  toolName: NangoCalendarApprovalTool;
  eventId: string;
  subject: string;
  start: string;
  end: string;
  description: string;
  attendees: string[];
}

export const readCalendarApprovalFields = (
  toolName: string,
  toolInput: Record<string, unknown>
): CalendarApprovalFields | null => {
  const leaf = resolveNangoApprovalLeafTool(toolName, toolInput);
  if (!CALENDAR_SET.has(leaf)) return null;
  const source =
    toolName === 'integrations.apps.write' && isRecord(toolInput.arguments)
      ? toolInput.arguments
      : toolInput;
  return {
    toolName: leaf as NangoCalendarApprovalTool,
    eventId: asString(source.event_id),
    subject: asString(source.subject),
    start: asString(source.start),
    end: asString(source.end),
    description: asString(source.description),
    attendees: parseAddressList(source.attendees),
  };
};

export const buildCalendarModifiedInput = (
  original: Record<string, unknown>,
  fields: CalendarApprovalFields
): Record<string, unknown> => {
  if (fields.toolName === 'integrations.calendar.delete') {
    return { ...original };
  }
  const source = sourceRecord(original);
  const patch: Record<string, unknown> = {};
  if ('subject' in source || fields.subject) patch.subject = fields.subject;
  if ('description' in source || fields.description) {
    patch.description = fields.description;
  }
  if ('attendees' in source || fields.attendees.length > 0) {
    patch.attendees = fields.attendees;
  }
  if (fields.start) patch.start = fields.start;
  if (fields.end) patch.end = fields.end;
  return applyFieldPatch(original, patch);
};

export interface FileApprovalFields {
  filename: string;
  folderPath: string;
}

export const readFileApprovalFields = (
  toolName: string,
  toolInput: Record<string, unknown>
): FileApprovalFields | null => {
  const leaf = resolveNangoApprovalLeafTool(toolName, toolInput);
  if (!FILE_SET.has(leaf)) return null;
  const source =
    toolName === 'integrations.apps.write' && isRecord(toolInput.arguments)
      ? toolInput.arguments
      : toolInput;
  return {
    filename: asString(source.filename),
    folderPath: asString(source.folder_path),
  };
};

export const buildFileModifiedInput = (
  original: Record<string, unknown>,
  fields: FileApprovalFields
): Record<string, unknown> => applyFieldPatch(original, { filename: fields.filename });

export interface WorkspaceWriteApprovalFields {
  leafTool: string;
  action: NangoWorkspaceWriteAction;
  target: 'document' | 'spreadsheet' | 'page' | 'message' | 'file';
  destination: string;
  preview: string;
}

const WORKSPACE_DESTINATION_KEYS = [
  'title',
  'name',
  'document_id',
  'spreadsheet_id',
  'page_id',
  'notebook_id',
  'section_id',
  'channel',
  'channel_id',
  'filename',
  'path',
] as const;

const WORKSPACE_PREVIEW_KEYS = ['text', 'content', 'body', 'html', 'message'] as const;
const WORKSPACE_PREVIEW_MAX = 280;

export const readWorkspaceWriteApprovalFields = (
  toolName: string,
  toolInput: Record<string, unknown>
): WorkspaceWriteApprovalFields | null => {
  const leaf = resolveNangoApprovalLeafTool(toolName, toolInput);
  if (nangoConnectorApprovalKind(toolName, toolInput) !== 'workspace') return null;
  const source = sourceRecord(toolInput);
  return {
    leafTool: leaf,
    action: workspaceWriteAction(leaf),
    target: workspaceWriteTarget(leaf),
    destination: firstStringField(source, WORKSPACE_DESTINATION_KEYS),
    preview: workspacePreview(source),
  };
};

const workspaceWriteAction = (leafTool: string): NangoWorkspaceWriteAction => {
  if (leafTool.includes('delete')) return 'delete';
  if (
    leafTool.endsWith('.create') ||
    leafTool.includes('.create_') ||
    leafTool.includes('create_page')
  ) {
    return 'create';
  }
  if (leafTool.includes('post') || leafTool.includes('send')) return 'send';
  return 'edit';
};

const workspaceWriteTarget = (
  leafTool: string
): WorkspaceWriteApprovalFields['target'] => {
  if (leafTool.includes('sheets') || leafTool.includes('sheet')) return 'spreadsheet';
  if (leafTool.includes('onenote')) return 'page';
  if (leafTool.includes('teams')) return 'message';
  if (leafTool.includes('files')) return 'file';
  return 'document';
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isDirectoryWritePayload(
  original: Record<string, unknown>
): original is Record<string, unknown> & { arguments: Record<string, unknown> } {
  return typeof original.tool === 'string' && isRecord(original.arguments);
}

function sourceRecord(original: Record<string, unknown>): Record<string, unknown> {
  return isDirectoryWritePayload(original) ? original.arguments : original;
}

function applyFieldPatch(
  original: Record<string, unknown>,
  patch: Record<string, unknown>
): Record<string, unknown> {
  if (isDirectoryWritePayload(original)) {
    return { ...original, arguments: { ...original.arguments, ...patch } };
  }
  return { ...original, ...patch };
}

function firstStringField(
  source: Record<string, unknown>,
  keys: readonly string[]
): string {
  for (const key of keys) {
    const value = asString(source[key]).trim();
    if (value) return value;
  }
  return '';
}

function workspacePreview(source: Record<string, unknown>): string {
  const text = firstStringField(source, WORKSPACE_PREVIEW_KEYS);
  if (text) {
    return text.length > WORKSPACE_PREVIEW_MAX
      ? `${text.slice(0, WORKSPACE_PREVIEW_MAX)}…`
      : text;
  }
  if (Array.isArray(source.row) || isRecord(source.row)) {
    const serialized = JSON.stringify(source.row);
    return serialized.length > WORKSPACE_PREVIEW_MAX
      ? `${serialized.slice(0, WORKSPACE_PREVIEW_MAX)}…`
      : serialized;
  }
  return '';
}
